import { connect } from 'react-redux';
import { store } from '../../..';
import { ApplicationState } from "../../../store";
import { ToxnotComponentProperties } from "../BaseApplicationComponents/ToxnotComponent";
import { ToxnotSignInRequiredComponent } from '../BaseApplicationComponents/ToxnotSignInRequiredComponent';
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { InventoryQuestionnaireType, SurveyResponseState } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates';
import { AISurveyAgentState, getActiveAIOperationFromState } from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import {
    Questionnaire,
    QuestionnairePageType,
    QuestionnaireQuestion,
    SurveyPageType,
    SupplierQuestionnaireData, QuestionType,
    DataCollectionAiAgentOperationType
} from '../../../store/Models/Surveys/SurveyDataModel';
import GroupMaterials from '../GroupMaterials';
import ProductOverview from '../ProductOverview';
import BomEditor from '../BomEditor';
import BatchResponse from '../BulkInputs/BulkInputs';
import SurveyResponseInstructions from '../../Survey/SurveyResponse/SurveyResponseInstructions';
import { InventoryDetailsResponse, PlausibilityData, isSurveyReadonly, isAiProcessingReadonly } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import SaveDataManager from '../../../utilities/SaveDataManager';
import { Result, ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { TeamRole } from '../../../store/Models/UserContext';
import { FileTableEntry } from '../../../store/Models/Documents/DocumentModels';
import QuestionnairePage from '../QuestionnairePage';
import AddMaterials from "../AddMaterials";
import { RegulationManager } from '../../../store/Managers/RegulationManager'

import './style.css';
import Question from '../Question';

type SurveyPageProps =
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState &
    { aiSurveyAgentState?: AISurveyAgentState };

class SurveyPage extends ToxnotSignInRequiredComponent<SurveyPageProps> {
    public _serviceClient: ToxnotServiceClient;
    private readonly _inventoryQuestionnaireSaveDataManager: SaveDataManager<InventoryDetailsResponse>;
    private readonly _supplierQuestionnaireSaveDataManager: SaveDataManager<Questionnaire>;
    private readonly baseQuestionnaire = 'BaseQuestionnaire';
    private completionModalShownForResponseId: string | null = null;
    private completionValidationVersion = 0;
    private isUnmounted = false;
    private readonly failedInventoryQuestionnaireSaves = new Set<string>();
    private supplierQuestionnaireSaveFailed = false;

    public constructor(props: SurveyPageProps) {
        super(props);

        this._serviceClient = new ToxnotServiceClient();
        this._inventoryQuestionnaireSaveDataManager = new SaveDataManager(this.saveInventoryQuestionnaire, this.props.appServices);
        this._supplierQuestionnaireSaveDataManager = new SaveDataManager(this.saveSupplierQuestionnaire, this.props.appServices);
    }

    protected renderConcreteComponent() {
        const supplierLevelQuestionnaire = SurveyResponseController.getSupplierLevelQuestionnaire();
        const activeAiOperation = getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey);

        // Check if survey is readonly due to AI operation
        const isReadonlyDueToAi = isAiProcessingReadonly(activeAiOperation);

        switch (this.props.navContext.mainNavIndex) {
            case SurveyPageType.Instructions:
               return <SurveyResponseInstructions />;
            case SurveyPageType.GroupInventories:
                return <GroupMaterials />;
            case SurveyPageType.Batch:
                return <BatchResponse />
            case SurveyPageType.SupplierQuestionnaire:
                const supplierQuestionnairePageProps = {
                    onResponseChange: this.onResponseChange,
                    questionsList: supplierLevelQuestionnaire?.questionnaire?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.questions,
                    pageTitle: supplierLevelQuestionnaire?.questionnaire?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.languageToTitleMap[this.props.insActiveLanguage] ?? supplierLevelQuestionnaire?.questionnaire?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.title ?? '',
                    regulationId: supplierLevelQuestionnaire?.questionnaire?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.regulationId,
                    inventoryQuestionnaireType: undefined,
                    fileList: this.props.coreData?.allSupplierFiles,
                    inventoryId: this.props.selectedInventory?.inventoryId,
                    questionsRevisionInstructions: supplierLevelQuestionnaire?.revisionRequest?.supplierQuestionRevisions,
                    pageRevisionInstructions: supplierLevelQuestionnaire?.revisionRequest?.generalRevisionInstructions,
                    onComponentDidMount: async () => this.props.updateSupplierQuestionnairePageVisited(this.props.navContext.surveyQuestionnairePageIndex)
                }

                return <QuestionnairePage
                    key={this.props.navContext.surveyQuestionnairePageIndex}
                    {...supplierQuestionnairePageProps}
                />

            case SurveyPageType.Inventory:
                if (this.props.navContext.surveyQuestionnairePageIndex === -1) {
                    return <ProductOverview />;
                }

                const files = this.props.selectedInventory?.fileList ?? [];

                // TODO: this doesn't do anything?
                files.map(f => f.inventoryId = this.props.selectedInventory?.inventoryId);

                const questionnairePageProps = {
                    onResponseChange: this.onResponseChange,
                    questionsList: this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex].questions,
                    questionRegulatoryData: this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex].questionRegulatoryData,
                    pageTitle: this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex].languageToTitleMap[this.props.insActiveLanguage] ?? this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex].title,
                    regulationId: this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.regulationId ?? "",
                    fileList: files,
                    inventoryId: this.props.selectedInventory?.inventoryId,
                    questionsRevisionInstructions: this.getQuestionnaireRevisionInstructions(),
                    pageRevisionInstructions: this.getPageRevisionInstructions(),
                    questionnairePageType: this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex].type,
                    inventoryQuestionnaireType: this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex],
                    onComponentDidMount: async () =>
                        this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex] === InventoryQuestionnaireType.BaseQuestionnaire ?
                            await this.props.updateBaseQuestionnairePageHasBeenVisited(this.props.navContext.surveyQuestionnaireIndex, this.props.navContext.surveyQuestionnairePageIndex, this.props.navContext.selectedInventoryPageIndex, true) :
                            await this.props.updateSelectSubInventoryCustomQuestionniarePageVisited(this.props.navContext.surveyQuestionnaireIndex, this.props.navContext.surveyQuestionnairePageIndex)
                };

                return <QuestionnairePage
                    key={this.props.navContext.surveyQuestionnaireIndex + "-" + this.props.navContext.surveyQuestionnairePageIndex}
                    {...questionnairePageProps}
                />;

            case SurveyPageType.Bom:
                const bomPageProps = {
                    pageRevisionInstructions: this.getPageRevisionInstructions()
                };

                return <BomEditor {...bomPageProps} />;
            case SurveyPageType.AddMaterials:
                return <AddMaterials/>;
            default:
                return null;
        }
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    public async componentDidMount() {
        this._inventoryQuestionnaireSaveDataManager.enable();
        this._supplierQuestionnaireSaveDataManager.enable();
    }

    public componentDidUpdate(prevProps: SurveyPageProps) {
        if (prevProps.responseId !== this.props.responseId || prevProps.navContext !== this.props.navContext) {
            ++this.completionValidationVersion;
        }
        if (prevProps.responseId !== this.props.responseId) {
            this.failedInventoryQuestionnaireSaves.clear();
            this.supplierQuestionnaireSaveFailed = false;
        }

        const responseWasComplete = SurveyResponseController.isOverallValid(prevProps.validationResult);
        const responseIsComplete = SurveyResponseController.isOverallValid(this.props.validationResult);

        if (prevProps.validationResult !== this.props.validationResult && !responseIsComplete) {
            ++this.completionValidationVersion;
        }

        if (prevProps.responseId === this.props.responseId &&
            !responseWasComplete &&
            responseIsComplete) {
            void this.showCompletionModalIfReady(responseWasComplete);
        }
    }

    public componentWillUnmount() {
        this.isUnmounted = true;
        ++this.completionValidationVersion;
    }

    private async responseChangedHandleNavigation(
        question: QuestionnaireQuestion | undefined | null,
        supplierLevelQuestionnaire: SupplierQuestionnaireData | undefined | null,
        value: string
    ) {
        const isSupplierQuestionnaire = this.props.navContext.mainNavIndex === SurveyPageType.SupplierQuestionnaire &&
            supplierLevelQuestionnaire !== null
            && supplierLevelQuestionnaire !== undefined;

        if (question?.nextPageIndexMap && value !== question?.value) {
            const nextJumpKey = value.split(":")[0];
            const nextJumpIndex = question.nextPageIndexMap[nextJumpKey];

            if (nextJumpIndex && nextJumpIndex !== this.props.navContext.surveyQuestionnairePageIndex) {
                let previousPageStack = [...this.props.navContext.previousPageStack];

                previousPageStack.push(this.props.navContext.surveyQuestionnairePageIndex);
                const mainInventoryList = SurveyResponseController.getMainSupplierFacingMaterialInventoryList();
                const paginationDetails = SurveyResponseController.coreValues(this.props.coreData);
                const totalInventories = paginationDetails.totalInventories;
                
                if (isSupplierQuestionnaire) {
                    if (nextJumpIndex >= supplierLevelQuestionnaire.questionnaire?.pages?.length) {

                        if (mainInventoryList.length >= SurveyResponseController.materiaListSizeGroupThreshold && (totalInventories > 1)) {
                            this.props.setMainNavPage(SurveyPageType.Batch);
                        } else if (mainInventoryList.length > 0) {
                            this.props.setSurveySelectedInventory(0);
                        }
                    } else {
                        this.props.setMainNavPage(SurveyPageType.SupplierQuestionnaire, this.props.navContext.surveyQuestionnaireIndex, nextJumpIndex, previousPageStack);
                    }
                }
                else if (nextJumpIndex >= this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages.length &&
                    this.props.navContext.surveyQuestionnaireIndex == this.props.surveyQuestionnairesList.length - 1 &&
                    this.props.navContext.inventoryNavIndex == mainInventoryList.length - 1) {
                    await this.showCompletionModalIfReady(false, true);
                    return;
                }
                else if (nextJumpIndex >= this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages.length &&
                    this.props.navContext.surveyQuestionnaireIndex == this.props.surveyQuestionnairesList.length - 1) {
                    this.props.setSurveySelectedInventory(this.props.navContext.inventoryNavIndex + 1)
                } else if (nextJumpIndex >= this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages.length &&
                    this.props.navContext.surveyQuestionnaireIndex < this.props.surveyQuestionnairesList.length - 1) {
                    this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, this.props.navContext.surveyQuestionnaireIndex + 1, 0, previousPageStack);
                } else {
                    this.props.setNavigationContext(this.props.navContext.mainNavIndex, this.props.navContext.inventoryNavIndex, this.props.navContext.surveyQuestionnaireIndex, nextJumpIndex, previousPageStack);
                }
            }
        }
    }

    private onResponseChange = async (
        value: string,
        valueComment: string,
        questionIndex: number,
        files?: FileTableEntry[],
        attachedFileIds?: string[]
    ): Promise<void> => {
        const responseId = this.props.responseId;
        const navContext = this.props.navContext;
        ++this.completionValidationVersion;
        const responseWasComplete = SurveyResponseController.isOverallValid(this.props.validationResult);
        const supplierLevelQuestionnaire = SurveyResponseController.getSupplierLevelQuestionnaire();
        const isSupplierQuestionnaire = this.props.navContext.mainNavIndex === SurveyPageType.SupplierQuestionnaire && supplierLevelQuestionnaire;
    
        const inventoryForThisChange = this.props.selectedInventory;
        const questionnaireIndexForThisChange = this.props.navContext.surveyQuestionnaireIndex;
        const pageIndexForThisChange = this.props.navContext.surveyQuestionnairePageIndex;
        let question: QuestionnaireQuestion | null = null;
        let shouldRevalidate = false; 

        if (isSupplierQuestionnaire) {
            const page = this.props.coreData?.response?.supplierQuestionnaireData?.questionnaire?.pages[pageIndexForThisChange];
            question = page?.questions[questionIndex] ? { ...page.questions[questionIndex] } : null;

            if (!question) return;

            if (question.type === QuestionType.ImportCMRT) {
                if (page) {
                    await this.processCmrtQuestionsFromCmrtFileJson(value, page.questions, files, attachedFileIds, true);
                    shouldRevalidate = true;
                }
            } else {
                this.props.updateSupplierQuestionnaire(value, questionIndex, files, attachedFileIds);
                if (question.summaryFieldKey === "cmrt_declaration_of_scope_or_class" && value !== question.value) {
                    shouldRevalidate = true;
                }
            }
            if (shouldRevalidate) {
                await this.revalidateSurvey();
            }
    
            await this._supplierQuestionnaireSaveDataManager.saveAsync(supplierLevelQuestionnaire.questionnaire);
    
        } else if (this.props.navContext.mainNavIndex === SurveyPageType.Inventory && inventoryForThisChange !== null) {
            const page = this.props.surveyQuestionnairesList[questionnaireIndexForThisChange]?.pages[pageIndexForThisChange];
            question = page?.questions[questionIndex] ? { ...page.questions[questionIndex] } : null;
            const regulationId = page?.regulationId ?? "";  // Extract regulationId for CalProp 65 check
    
            if (!question) return;
    
            if (question.type === QuestionType.ImportCMRT) {
                await this.processCmrtQuestionsFromCmrtFileJson(value, page.questions, files, attachedFileIds, false);
            } else {
                this.props.updateQuestion(value, valueComment, questionIndex, files, attachedFileIds);
            }
            await this._inventoryQuestionnaireSaveDataManager.saveAsync(inventoryForThisChange);

            this.props.updateQuestionRequired([...this.props.surveyQuestionnairesList]);
        }
        
        if (this.isUnmounted || this.props.responseId !== responseId || this.props.navContext !== navContext) {
            return;
        }
        await this.responseChangedHandleNavigation(question, supplierLevelQuestionnaire, value);
        await this.showCompletionModalIfReady(responseWasComplete);
    };

    private async showCompletionModalIfReady(responseWasComplete: boolean, isQuestionnaireEnd = false) {
        if (responseWasComplete && !isQuestionnaireEnd) {
            return;
        }

        const surveyState = store.getState().surveyResponseState;
        const canCheckCompletion = (state: SurveyResponseState | null | undefined) =>
            !this.isUnmounted && !!state?.responseId && !!state.coreData &&
            SurveyResponseController.isOverallValid(state.validationResult) &&
            (isQuestionnaireEnd || (this.completionModalShownForResponseId !== state.responseId &&
                state.coreData.request?.requireValidationOnResponse)) &&
            !this._inventoryQuestionnaireSaveDataManager.hasPendingSaves() &&
            !this._supplierQuestionnaireSaveDataManager.hasPendingSaves() &&
            this.failedInventoryQuestionnaireSaves.size === 0 && !this.supplierQuestionnaireSaveFailed &&
            state.coreData.supplierActionAvailability?.canSubmit !== false &&
            !isSurveyReadonly(state.coreData, getActiveAIOperationFromState(
                store.getState().aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey));

        if (!surveyState?.coreData || !canCheckCompletion(surveyState)) {
            return;
        }

        const validationVersion = ++this.completionValidationVersion;
        // A recently edited BOM may be on a different material page by the time validation finishes.
        const response = surveyState.coreData.response;
        const groups = Object.values(response.groupedResponseInventories)
            .filter(group => group.groupInventoryId !== null);
        const groupedInventoryIds = new Set<string>();
        groups.forEach(group => group.responderOwnedResponseInventoryIdList.forEach(id => groupedInventoryIds.add(id)));
        const inventoryIds = Array.from(new Set([
            ...response.inventoryDataList
                .map(inventory => inventory.responderOwnedResponseInventoryId)
                .filter(inventoryId => !groupedInventoryIds.has(inventoryId)),
            ...groups.map(group => group.groupInventoryId!)
        ]));

        try {
            // Confirm persisted answers using the existing API, independently of BOM validation scheduling.
            const result = await this._serviceClient.validateDataCollectionResponse(
                surveyState.responseId, inventoryIds, surveyState.coreData.extensionRequest.inventoryIds);
            const latestSurveyState = store.getState().surveyResponseState;

            if (!result.success || !result.data ||
                validationVersion !== this.completionValidationVersion ||
                latestSurveyState?.responseId !== surveyState.responseId ||
                latestSurveyState?.navContext !== surveyState.navContext ||
                !canCheckCompletion(latestSurveyState) ||
                !SurveyResponseController.isOverallValid(result.data)) {
                return;
            }

            this.completionModalShownForResponseId = surveyState.responseId;
            this.showModal("submit-survey-response-modal");
        } catch (error) {
            console.error("Error checking survey completion:", error);
        }
    }

    private async revalidateSurvey(){
        try {
            const invalidateResult = await this._serviceClient.invalidateSurveyResponses(this.props.responseId);
            
            if (invalidateResult.success) {
                // First save the questionnaire
                const supplierLevelQuestionnaire = SurveyResponseController.getSupplierLevelQuestionnaire();
                if (supplierLevelQuestionnaire && supplierLevelQuestionnaire.questionnaire) {
                    await this._supplierQuestionnaireSaveDataManager.saveAsync(supplierLevelQuestionnaire.questionnaire);
                    
                    if (this.props.selectedInventory) {
                        await this._serviceClient.inventorySummaryDataQuestionnaireResponseUpdate(
                            this.props.responseId,
                            this.props.selectedInventory,
                            this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]
                        );
                    }
                }
                
                const currentInventoryIds = SurveyResponseController.getMainSupplierFacingMaterialInventoryList()
                    .map(inv => inv.inventoryId);
                    
                // Finally call the existing action to update validation results
                await this.props.updateValidationResult(currentInventoryIds);
            }
        } catch (error) {
            console.error("Error during survey validation process:", error);
        }
    }
    // Private method to process questions from JSON
    private processCmrtQuestionsFromCmrtFileJson(value: string, questions: any[], files?: FileTableEntry[], attachedFileIds?: string[], isSupplierQuestionnaire: boolean = false): void {
        let index: number = 0;
        let parsedObject = JSON.parse(value);
    
        questions.forEach((question) => {
            const key = question.summaryFieldKey;
            if(key === "cmrt_files")
            {
                const fileId = parsedObject["cmrt_files_0"] || parsedObject[parsedObject.length - 1];
                if(fileId !== undefined){
                    if (!attachedFileIds?.includes(fileId)) {
                        attachedFileIds?.push(fileId);
                    }
                    if (isSupplierQuestionnaire) {
                        this.props.updateSupplierQuestionnaire(fileId, index, files, attachedFileIds);
                    } else {
                        this.props.updateQuestion(fileId, "", index, files, attachedFileIds);
                    }
                }
            }
            else if (key in parsedObject) {
                const value = parsedObject[key];
                if (isSupplierQuestionnaire) {
                    this.props.updateSupplierQuestionnaire(value, index, files, attachedFileIds);
                } else {
                    this.props.updateQuestion(value, "", index, files, attachedFileIds);
                }
            }
            index++;
        });
    }

    private saveInventoryQuestionnaire = async (inventory: InventoryDetailsResponse) => {
        if (isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey))) {
            return true;
        }

        let apiResult: Result;
        const responseId = this.props.responseId;

        const questionnaireType = this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex];

        if (questionnaireType === InventoryQuestionnaireType.BaseQuestionnaire ||
            questionnaireType.toString() === this.baseQuestionnaire) {

            apiResult = await this._serviceClient.inventorySummaryDataQuestionnaireResponseUpdate(this.props.responseId, inventory, this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]);
        }
        else {
            apiResult = await this._serviceClient.questionnaireResponseUpdate(
                this.props.responseId,
                inventory.inventoryId,
                this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex] === InventoryQuestionnaireType.AttestationQuestionnaire,
                this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex],
                this.props.selectedInventory?.fileList ?? []
            );
        }

        if (this.props.responseId !== responseId || this.isUnmounted) {
            return apiResult.success;
        }
        if (apiResult.success) {
            this.failedInventoryQuestionnaireSaves.delete(inventory.inventoryId);
        } else {
            this.failedInventoryQuestionnaireSaves.add(inventory.inventoryId);
        }
        if (this.props.selectedInventory?.inventoryId) {
            await this.props.updateValidationResult([this.props.selectedInventory?.inventoryId]);
        }

        return apiResult.success;
    }

    private saveSupplierQuestionnaire = async () => {
        if (isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey))) {
            return true;
        }

        const responseId = this.props.responseId;
        const apiResult = await this._serviceClient.questionnaireResponseUpdate(
            this.props.responseId,
            "",
            this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex] === InventoryQuestionnaireType.AttestationQuestionnaire,
            this.props.coreData!.response.supplierQuestionnaireData!.questionnaire,
            this.props.coreData?.response.supplierQuestionnaireData?.files ?? []
        );

        if (this.props.responseId !== responseId || this.isUnmounted) {
            return apiResult.success;
        }
        this.supplierQuestionnaireSaveFailed = !apiResult.success;
        await this.props.updateSupplierQuestionnaireValidationResult();

        return apiResult.success;
    }

    private getQuestionnaireRevisionInstructions() {
        if (this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex] == InventoryQuestionnaireType.AttestationQuestionnaire) {
            return this.props.selectedInventory?.revisionRequest?.attestationQuestionnaireQuestionRevisions;
        } else if (this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex] == InventoryQuestionnaireType.CustomQuestionnaire) {
            return this.props.selectedInventory?.revisionRequest?.bomQuestionnaireQuestionRevisions;
        }
    }

    private getPageRevisionInstructions() {
        if (this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex] == InventoryQuestionnaireType.BaseQuestionnaire)
        {
            switch (this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.type) {
                case QuestionnairePageType.CaProp65:
                    return this.props.selectedInventory?.revisionRequest?.dynamicRegulationRevisionInstructions[RegulationManager.CaProp65RegulationId];
                case QuestionnairePageType.Circularity:
                    return this.props.selectedInventory?.revisionRequest?.circularityRevisionInstructions;
                case QuestionnairePageType.ConflictMinerals:
                    return this.props.selectedInventory?.revisionRequest?.dynamicRegulationRevisionInstructions[RegulationManager.ConflictMineralsRegulationId];
                case QuestionnairePageType.ConflictMineralsV2:
                    return this.props.selectedInventory?.revisionRequest?.dynamicRegulationRevisionInstructions[RegulationManager.ConflictMineralsV2RegulationId];
                case QuestionnairePageType.DynamicRegulation:
                    return this.props.selectedInventory?.revisionRequest?.dynamicRegulationRevisionInstructions[this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex]?.pages[this.props.navContext.surveyQuestionnairePageIndex]?.regulationId ?? ""];
                case QuestionnairePageType.ReachAuthorisationCompliance:
                    return this.props.selectedInventory?.revisionRequest?.dynamicRegulationRevisionInstructions[RegulationManager.ReachSvhcAuthorizationRegulationId];
                case QuestionnairePageType.ReachAuthorisationV2Compliance:
                    return this.props.selectedInventory?.revisionRequest?.dynamicRegulationRevisionInstructions[RegulationManager.ReachSvhcAuthorizationV2RegulationId];
                case QuestionnairePageType.REACHCompliance:
                    return this.props.selectedInventory?.revisionRequest?.dynamicRegulationRevisionInstructions[RegulationManager.ReachSvhcCandidateRegulationId];
                case QuestionnairePageType.RoHSCompliance:
                    return this.props.selectedInventory?.revisionRequest?.dynamicRegulationRevisionInstructions[RegulationManager.RohsRegulationId];
                case QuestionnairePageType.Sustainability:
                case QuestionnairePageType.SustainabilityEmbodiedCarbon:
                case QuestionnairePageType.SustainabilityWaterUse:
                case QuestionnairePageType.SustainabilityAssessment:
                case QuestionnairePageType.SustainabilityOptimization:
                case QuestionnairePageType.SustainabilityTransparency:
                    return this.props.selectedInventory?.revisionRequest?.sustainabilityRevisionInstructions;
                case QuestionnairePageType.VocAll:
                case QuestionnairePageType.VocAssessment:
                case QuestionnairePageType.VocOptimization:
                case QuestionnairePageType.VocTransparency:
                    return this.props.selectedInventory?.revisionRequest?.vocRevisionInstructions;
                case QuestionnairePageType.BOM:
                    return this.props.selectedInventory?.revisionRequest?.bomQuestionnaireQuestionRevisions;
                default:
                    return null;
            }
        } else if (this.props.navContext.mainNavIndex == SurveyPageType.Bom) {
            return this.props.selectedInventory?.revisionRequest?.bomColumnRevisions;
        }
    }
}

export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, aiSurveyAgentState: state.aiSurveyAgentState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(SurveyPage as any);
